import { OfferPointsComponent } from '../../../components/offer-points/offer-points.component';
import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { SubscriptionType } from 'src/app/types/subscription';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { LicenseApiService } from 'src/app/services/license-api.service';
import {
  clearNegotiatedOfferSelection,
  saveMunicipalityPlanSelection,
  saveNegotiatedOfferSelection,
} from 'src/app/pages/onboarding/municipality-plan-selection';
import { NegotiatedOffer } from 'src/app/types/rest-api';
import { formatRappenChf } from 'src/app/utils/currency-formatter';
import { FreePlanJobPostingDialogComponent } from 'src/app/components/dialogs/free-plan-job-posting-dialog/free-plan-job-posting-dialog.component';
import { SchoolUnitOffersComponent } from 'src/app/pages/onboarding/school-unit-offers/school-unit-offers.component';

type Duration = 1 | 2 | 3;

/* ------------------------------------------------------------------ */
/*  Modern single-card types (matches onboarding)                     */
/* ------------------------------------------------------------------ */

type LeistungCell =
  | { kind: 'check' }
  | { kind: 'premium' }
  | { kind: 'text'; text: string; variant: 'blue' | 'muted' | 'body' };

type LeistungBlock =
  | { type: 'employerBanner'; title: string; subtitle: string }
  | { type: 'section'; title: string }
  | { type: 'row'; label: string; hint?: string; cell: LeistungCell };

/* ------------------------------------------------------------------ */
/*  Legacy two-card types (offers view without role)                   */
/* ------------------------------------------------------------------ */

interface PricingPlan {
  name: string;
  type: SubscriptionType;
  monthlyPrice: number;
  description: string;
  recommended?: boolean;
  ctaLabel: string;
}

interface FeatureRow {
  label: string;
  values: string[];
  hint?: string;
}

interface FeatureSection {
  title: string;
  subtitle?: string;
  icon?: string;
  rows: FeatureRow[];
}

@Component({
  selector: 'app-order-process-products',
  templateUrl: './order-process-products.component.html',
  styleUrls: ['./order-process-products.component.scss'],
  imports: [
    OfferPointsComponent,
    DatePipe,
    MatIcon,
    MatProgressSpinner,
    SchoolUnitOffersComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class OrderProcessProductsComponent implements OnInit {
  @Output() selectOffer = new EventEmitter<SubscriptionType>();
  private readonly licenseApi = inject(LicenseApiService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** When set, shows the modern single-card UI for that role. */
  role = input<'schuleinheit' | 'schulgemeinde' | null>(null);

  /** Same Kostenlos + Schuleinheit step as onboarding `SchoolUnitOffersComponent` (not legacy `.schools` cards). */
  schoolUnitOffersOnboardingLayout = input(false);

  /**
   * Fachkraft upgrade only. The negotiated offer replaces the catalogue, and accepting it
   * is handled by the ordering step — never enable it where an organisation already exists,
   * because the deal's order would then not be attached to it.
   */
  readonly showNegotiatedOffer = input(false);

  /** Negotiated Schulgemeinde offer for the signed-in user; replaces the plan card. */
  readonly offer = signal<NegotiatedOffer | null>(null);
  readonly offerLoading = signal(false);
  readonly formatRappenChf = formatRappenChf;

  planOrderLoading = false;
  planOrderError: string | null = null;

  selectedDuration: Duration = 1;
  extraAdmins = 0;
  totalSchoolUnits = 1;

  servicesExpanded = true;

  readonly discounts: Record<Duration, number> = { 1: 0, 2: 0.05, 3: 0.1 };

  ngOnInit(): void {
    // The Schuleinheit offer lives in the embedded `SchoolUnitOffersComponent`.
    if (!this.showNegotiatedOffer() || this.role() !== 'schulgemeinde') {
      return;
    }
    this.offerLoading.set(true);
    this.licenseApi
      .getNegotiatedOffer()
      .pipe(
        timeout(8000),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(offer => {
        this.offer.set(
          offer?.license_type === 'SCHOOL_AUTHORITY_SUBSCRIPTION' ? offer : null
        );
        this.offerLoading.set(false);
      });
  }

  limitLabel(value: number | null): string {
    return value === null ? 'unbegrenzt' : String(value);
  }

  onAcceptOffer(): void {
    const offer = this.offer();
    if (!offer) return;
    saveNegotiatedOfferSelection(offer);
    this.selectOffer.emit('SCHOOL_S');
  }

  /* ── Role-based getters ──────────────────────────────────────────── */

  get planName(): string {
    return this.role() === 'schulgemeinde' ? 'Schulgemeinde' : 'Schuleinheit';
  }

  get baseMonthlyChf(): number {
    return this.role() === 'schulgemeinde' ? 69 : 49;
  }

  get planDescription(): string {
    return this.role() === 'schulgemeinde'
      ? 'Ganzheitlicher Auftritt für Gemeinde, Schuleinheiten\nund Betreuung'
      : 'Die flexible Lösung für deine Schuleinheit';
  }

  get hasSchoolUnits(): boolean {
    return this.role() === 'schulgemeinde';
  }

  get adminHint(): string {
    return this.role() === 'schulgemeinde'
      ? '1 je Gemeinde & Schuleinheit inkl., weitere 9.90 Fr./Monat'
      : '1 Inklusive, weitere 9.90 Fr./Monat';
  }

  get extraSchoolUnits(): number {
    return Math.max(0, this.totalSchoolUnits - 1);
  }

  get monthlyTotalChf(): number {
    let total = this.baseMonthlyChf + this.extraAdmins * 9.9;
    if (this.hasSchoolUnits) {
      total += this.extraSchoolUnits * 14.9;
    }
    return total;
  }

  get annualTotalChf(): number {
    const baseAnnualSubtotal = this.baseMonthlyChf * 12 * this.selectedDuration;
    const addonsAnnualSubtotal =
      (this.extraAdmins * 9.9 +
        (this.hasSchoolUnits ? this.extraSchoolUnits * 14.9 : 0)) *
      12 *
      this.selectedDuration;
    return (
      baseAnnualSubtotal * (1 - this.currentDiscount) + addonsAnnualSubtotal
    );
  }

  get currentDiscount(): number {
    return this.discounts[this.selectedDuration];
  }

  get discountPercent(): number {
    return Math.round(this.currentDiscount * 100);
  }

  get leistungBlocks(): LeistungBlock[] {
    return this.role() === 'schulgemeinde'
      ? this.schulgemeindeBlocks
      : this.schuleinheitBlocks;
  }

  /* ── Actions ─────────────────────────────────────────────────────── */

  selectDuration(d: Duration) {
    this.selectedDuration = d;
  }

  incrementAdmins() {
    if (this.extraAdmins < 50) this.extraAdmins++;
  }

  decrementAdmins() {
    if (this.extraAdmins > 0) this.extraAdmins--;
  }

  incrementSchoolUnits() {
    // 1 unit is included in the base plan, allow up to 50 additional units.
    if (this.totalSchoolUnits < 51) this.totalSchoolUnits++;
  }

  decrementSchoolUnits() {
    if (this.totalSchoolUnits > 1) this.totalSchoolUnits--;
  }

  toggleServices() {
    this.servicesExpanded = !this.servicesExpanded;
  }

  async selectPaid() {
    clearNegotiatedOfferSelection();
    saveMunicipalityPlanSelection({
      subscriptionType: 'SCHOOL_S',
      durationYears: this.selectedDuration,
      extraAdmins: this.extraAdmins,
      totalSchoolUnits: this.totalSchoolUnits,
      monthlyTotalChf: this.monthlyTotalChf,
      annualTotalChf: this.annualTotalChf,
      discountPercent: this.discountPercent,
    });

    this.selectOffer.emit('SCHOOL_S');
  }

  relaySchoolUnitOnboardingOffer(t: SubscriptionType) {
    this.selectOffer.emit(t);
  }

  selectFree() {
    clearNegotiatedOfferSelection();
    saveMunicipalityPlanSelection({
      subscriptionType: 'SCHOOL_FREE',
      durationYears: 1,
      extraAdmins: 0,
      totalSchoolUnits: 1,
      monthlyTotalChf: 0,
      annualTotalChf: 0,
      discountPercent: 0,
    });
    this.selectOffer.emit('SCHOOL_FREE');
  }

  /* ── Leistung data ───────────────────────────────────────────────── */

  private readonly schulgemeindeBlocks: LeistungBlock[] = [
    {
      type: 'employerBanner',
      title: 'Attraktiver Arbeitgeberauftritt durch Profil',
      subtitle: 'Präsentiere deine Schule attraktiv und professionell',
    },
    {
      type: 'row',
      label: 'Sichtbarkeit & Ranking',
      hint: 'Bessere Platzierung und erhöhte Reichweite',
      cell: { kind: 'premium' },
    },
    {
      type: 'row',
      label: 'Gemeinde, Schule und Betreuung',
      hint: 'Umfassendes Profil mit Bildern, Videos, Werten und Team-Vorstellung',
      cell: { kind: 'premium' },
    },
    { type: 'section', title: 'Stellenausschreibungen & Reichweite' },
    {
      type: 'row',
      label: 'Stellenausschreibungen',
      cell: { kind: 'text', text: 'Unbegrenzt', variant: 'blue' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Stellvertretung',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Festanstellung',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Automatisches Multiposting',
      hint: 'Stellen erscheinen automatisch Premium auf: Bildungsstellen, PHZH-Stellenbörse, VSLCH, Google Jobs und mehr.',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Social Media Sharing-Tools',
      hint: 'Einfaches Teilen auf Instagram & LinkedIn (eigenes Profil benötigt)',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Website-Einbindung',
      hint: 'Schulprofil & Stellen direkt auf deiner Homepage einbinden (auf Anfrage)',
      cell: { kind: 'check' },
    },
    { type: 'section', title: 'Bewerbungsmanagement & Prozesse' },
    {
      type: 'row',
      label: 'Bewerbungsmanagement-System',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Bearbeitung von Inseraten',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Zeit sparen mit Automatisierungen',
      cell: { kind: 'check' },
    },
    { type: 'section', title: 'Organisation & Verwaltung' },
    { type: 'row', label: 'Schulgemeinde-Account', cell: { kind: 'check' } },
    {
      type: 'row',
      label: 'Zusätzliche Schuleinheitsaccounts',
      cell: { kind: 'text', text: '+14.90 CHF/Monat', variant: 'muted' },
    },
    {
      type: 'row',
      label: 'Standorte der Schuleinheit',
      cell: { kind: 'text', text: 'Unbegrenzt', variant: 'blue' },
    },
    {
      type: 'row',
      label: 'Admin-Accounts',
      cell: { kind: 'text', text: '1 inkl. pro Einheit', variant: 'body' },
    },
    {
      type: 'row',
      label: 'Zusätzliche Admins',
      cell: { kind: 'text', text: '+9.90 CHF/Monat', variant: 'muted' },
    },
    { type: 'section', title: 'Sicherheit & Support' },
    {
      type: 'row',
      label: 'Maximaler Datenschutz nach DSG & DSGVO',
      cell: { kind: 'check' },
    },
    { type: 'row', label: 'Persönlicher Support', cell: { kind: 'check' } },
    { type: 'row', label: 'Online-Kurs für Admins', cell: { kind: 'check' } },
  ];

  private readonly schuleinheitBlocks: LeistungBlock[] = [
    {
      type: 'employerBanner',
      title: 'Attraktiver Arbeitgeberauftritt durch Profil',
      subtitle: 'Präsentiere deine Schule attraktiv und professionell',
    },
    {
      type: 'row',
      label: 'Sichtbarkeit & Ranking',
      hint: 'Bessere Platzierung und erhöhte Reichweite',
      cell: { kind: 'premium' },
    },
    {
      type: 'row',
      label: 'Schule, Standorte und Profil',
      hint: 'Umfassendes Profil mit Bildern, Videos, Werten und Team-Vorstellung',
      cell: { kind: 'premium' },
    },
    { type: 'section', title: 'Stellenausschreibungen & Reichweite' },
    {
      type: 'row',
      label: 'Stellenausschreibungen',
      cell: { kind: 'text', text: 'Unbegrenzt', variant: 'blue' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Stellvertretung',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Festanstellung',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Automatisches Multiposting',
      hint: 'Deine Stelle erscheint automatisch auf bildungsstellen.ch, vslch.ch, Google und mehr',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Social Media Sharing-Tools',
      hint: 'Einfaches Teilen auf Instagram & LinkedIn',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Website-Einbindung',
      hint: 'Schulprofil & Stellen direkt auf deiner Homepage einbinden',
      cell: { kind: 'check' },
    },
    { type: 'section', title: 'Bewerbungsmanagement & Prozesse' },
    {
      type: 'row',
      label: 'Bewerbungsmanagement-System',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Bearbeitung von Inseraten',
      cell: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Zeit sparen mit Automatisierungen',
      cell: { kind: 'check' },
    },
    { type: 'section', title: 'Organisation & Verwaltung' },
    {
      type: 'row',
      label: 'Standorte der Schuleinheit',
      cell: { kind: 'text', text: 'Bis zu 5', variant: 'blue' },
    },
    {
      type: 'row',
      label: 'Admin-Accounts',
      cell: { kind: 'text', text: '1 Inklusive', variant: 'body' },
    },
    {
      type: 'row',
      label: 'Zusätzliche Admins',
      cell: { kind: 'text', text: '+9.90 CHF/Monat', variant: 'muted' },
    },
    { type: 'section', title: 'Sicherheit & Support' },
    {
      type: 'row',
      label: 'Maximaler Datenschutz nach DSG & DSGVO',
      cell: { kind: 'check' },
    },
    { type: 'row', label: 'Persönlicher Support', cell: { kind: 'check' } },
    { type: 'row', label: 'Online-Kurs für Admins', cell: { kind: 'check' } },
  ];

  /* ================================================================ */
  /*  Legacy two-card view (offers without role)                      */
  /* ================================================================ */

  featuresExpanded = false;

  readonly plans: PricingPlan[] = [
    {
      name: 'Kostenlos',
      type: 'SCHOOL_FREE',
      monthlyPrice: 0,
      description: 'Jetzt ausprobieren! Einzelne Stellenanzeigen.',
      ctaLabel: 'Kostenlos starten',
    },
    {
      name: 'Schuleinheit',
      type: 'SCHOOL_S',
      monthlyPrice: 49,
      description: 'Die flexible Lösung',
      recommended: true,
      ctaLabel: 'Auswählen',
    },
  ];

  readonly featureSections: FeatureSection[] = [
    {
      title: 'Stellenausschreibungen & Reichweite',
      rows: [
        {
          label: 'Stellenausschreibungen',
          values: ['Kostenlos', 'Unbegrenzt'],
        },
        { label: 'Einzelinserat: Direktwerbung', values: ['25 CHF', '✓'] },
        { label: 'Einzelinserat: Festanstellung', values: ['100 CHF', '✓'] },
        {
          label: 'Automatisches Multiposting',
          values: ['✓', '✓'],
          hint: 'Deine Stellenangebote automatisch auf Stellenbörsen, edu-jobs.ch, Google und mehr',
        },
        {
          label: 'Website-Einbindung',
          values: ['✓', '✓'],
          hint: 'Integriere deine offenen Stellen auf deiner Homepage mittels iFrame/Plugin',
        },
        {
          label: 'Social Media Sharing-Tools',
          values: ['✓', '✓'],
          hint: 'Teile deine Ausschreibungen einfach & direkt mit Links für alle Kanäle',
        },
      ],
    },
    {
      title: 'Attraktiver Arbeitgeberauftritt durch Profil',
      subtitle: 'Präsentiere deine Schule attraktiv und professionell',
      icon: 'star',
      rows: [
        {
          label: 'Sichtbarkeit & Ranking',
          values: ['Standard', 'Premium'],
          hint: 'Bessere Platzierung vor allen Kostenlosen',
        },
        {
          label: 'Schule, Standorte und Profil',
          values: ['Basis', 'Erweitert'],
          hint: 'Umfangreiche Profile: Bilder, Videos, Werten und Team-Vorstellungen',
        },
      ],
    },
    {
      title: 'Bewerbungsmanagement & Prozesse',
      rows: [
        { label: 'Bewerbungsmanagement-System', values: ['✓', '✓'] },
        { label: 'Bearbeitung von Inseraten', values: ['✓', '✓'] },
        { label: 'Zeit sparen mit Automatisierungen', values: ['✓', '✓'] },
      ],
    },
    {
      title: 'Organisation & Verwaltung',
      rows: [
        {
          label: 'Standorte der Schuleinheit',
          values: ['Bis zu 2', 'Bis zu 5'],
        },
        { label: 'Admin-Accounts', values: ['1', '1 Inklusive'] },
        { label: 'Zusätzliche Admins', values: ['—', '+9.90 CHF/Monat'] },
        { label: 'Schuleinheiten', values: ['—', '✓'] },
      ],
    },
    {
      title: 'Sicherheit & Support',
      rows: [
        { label: 'Maximaler Datenschutz nach DSG & DSGVO', values: ['✓', '✓'] },
        { label: 'Persönlicher Support', values: ['✓', '✓'] },
        { label: 'Online-Kurs für Admins', values: ['—', '✓'] },
      ],
    },
  ];

  readonly planNames = this.plans.map(p => p.name);

  select(type: SubscriptionType) {
    if (type === 'SCHOOL_FREE') {
      this.dialog
        .open(FreePlanJobPostingDialogComponent, {
          data: { variant: 'onboardingConfirm' },
          maxWidth: '520px',
          autoFocus: false,
        })
        .afterClosed()
        .subscribe(proceed => {
          if (!proceed) return;
          saveMunicipalityPlanSelection({
            subscriptionType: 'SCHOOL_FREE',
            durationYears: 1,
            extraAdmins: 0,
            totalSchoolUnits: 1,
            monthlyTotalChf: 0,
            annualTotalChf: 0,
            discountPercent: 0,
          });
          this.selectOffer.emit(type);
        });
      return;
    }
    if (type === 'SCHOOL_S') {
      saveMunicipalityPlanSelection({
        subscriptionType: 'SCHOOL_S',
        durationYears: this.selectedDuration,
        extraAdmins: this.extraAdmins,
        totalSchoolUnits: this.totalSchoolUnits,
        monthlyTotalChf: this.legacyTotalMonthlyPrice,
        annualTotalChf: this.legacyTotalAnnualPrice,
        discountPercent: this.discountPercent,
      });
    }
    this.selectOffer.emit(type);
  }

  legacyIncrementAdmins() {
    if (this.extraAdmins < 50) this.extraAdmins++;
  }

  legacyDecrementAdmins() {
    if (this.extraAdmins > 0) this.extraAdmins--;
  }

  getAnnualPrice(monthlyPrice: number): number {
    return monthlyPrice * 12 * (1 - this.currentDiscount);
  }

  hasDiscount(plan: PricingPlan): boolean {
    return plan.monthlyPrice > 0 && this.currentDiscount > 0;
  }

  get legacyTotalMonthlyPrice(): number {
    return 49 + this.extraAdmins * 9.9;
  }

  get legacyTotalAnnualPrice(): number {
    const baseAnnualSubtotal = 49 * 12 * this.selectedDuration;
    const addonsAnnualSubtotal =
      this.extraAdmins * 9.9 * 12 * this.selectedDuration;
    return (
      baseAnnualSubtotal * (1 - this.currentDiscount) + addonsAnnualSubtotal
    );
  }

  toggleFeatures() {
    this.featuresExpanded = !this.featuresExpanded;
  }

  isCheckOrCross(value: string): 'check' | 'cross' | null {
    if (value === '✓') return 'check';
    if (value === '✗') return 'cross';
    return null;
  }

  private readonly badgeValues = new Set(['Premium', 'Erweitert']);
  private readonly blueValues = new Set(['Unbegrenzt', 'Inklusive']);

  isBadge(value: string): boolean {
    return this.badgeValues.has(value);
  }

  isBlueText(value: string): boolean {
    return this.blueValues.has(value);
  }

  isPlainText(value: string): boolean {
    return (
      !this.isCheckOrCross(value) &&
      !this.isBadge(value) &&
      !this.isBlueText(value)
    );
  }

  isDash(value: string): boolean {
    return value === '—';
  }
}
